<template>
    <div>
        <div class="stock-wrap">
            <div class="background-image" :class="{ 'video-background-image': image.type === 'video'}" :style="imageBackgroundStyle"/>
            <img v-if="image.type === 'image'" :src="image.url" :alt="image.description" @load="stockLoaded">
            <div v-else class="video-wrap" :class="image.orientation">
                <video-player ref="video" :fallback-video-url="image.video_url" :poster-url="image.url" @timeupdate="timeElapsed" @load="stockLoaded"/>
                <div ref="timecode" class="video-timestamp" :style="{'left':videoTimecodeOffset+'px'}">
                    <span class="time-text">{{ secondsToTimestamp(videoElapsedTime) }} / {{ secondsToTimestamp(image.duration) }}</span>
                </div>
            </div>
        </div>
        <div class="background-selection">
            <div id="background-selection-contents">
                <span class="heading">View on a different background colour</span>
                <div class="background-colors">
                    <a v-for="color in backgroundColors" :key="color.name" class="color-pick-btn" :class="{selected: (selectedBackgroundColor===color)}" :title="color.name" :style="`background-color:${color.color}`"
                       @click="selectBackgroundColor(color)"
                    />
                </div>
            </div>
            <div id="background-selection-collection-btn">
                <nuxt-link v-if="image.in_collection" class="btn btn-outline-primary" :to="{ name: 'collections' }">Already in collection ></nuxt-link>
                <a v-else class="btn btn-outline-primary" @click="addToCollections">
                    <b-icon-folder-plus/>
                    Add to collection</a>
            </div>
        </div>
        <div class="row">
            <div class="col-lg-6 col-md-12">
                <h1 class="image-title">
                    {{ image.description }}
                </h1>
                <div class="image-tags">
                    <nuxt-link v-for="tag in image.tags" :key="tag.id" class="tag" :to="`/search?term=${ tag.name}`">{{ tag.name }}</nuxt-link>
                </div>
                <div v-if="image.curatedAlbum" class="curated-album-info">
                    Part of our
                    <nuxt-link :to="`/curated-album/${image.curatedAlbum.id}`">
                        {{ image.curatedAlbum.name }}
                    </nuxt-link>
                    curated album
                </div>
                <div v-if="image.location" class="image-genres mb-0">
                    <span class="heading">Location</span>
                    <span>{{ image.location }}</span>
                </div>
                <div class="image-genres">
                    <span class="heading">Genres</span>
                    <span>{{ image.genres.map(genre => genre.name).join(', ') }}</span>
                </div>
                <div class="contributor-card">
                    <div class="title">
                        By purchasing this {{ image.type }} you are directly helping local artists thrive. {{ image.type.charAt(0).toUpperCase() + image.type.slice(1) }} by:
                    </div>
                    <div class="contributor-details">
                        <div v-if="image.contributor.profile_image_url" class="contributor-profile">
                            <nuxt-link :to="{ name: 'contributor-profile', params: {slug: image.contributor.slug} }">
                                <img :src="image.contributor.profile_image_url">
                            </nuxt-link>
                        </div>
                        <div class="contributor-info">
                            <nuxt-link :to="{ name: 'contributor-profile', params: {slug: image.contributor.slug} }">
                                <h2>{{ image.contributor.name }}.</h2>&nbsp;<span class="region">{{ image.contributor.location }}</span>
                            </nuxt-link>
                            <h3>#{{ image.code }}</h3>
                            <span v-if="image.model_release" class="release">Model Release obtained</span>
                            <span v-if="image.property_release" class="release">Property Release obtained</span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6 col-md-12 d-none d-md-block">
                <ImagesFromShoot ref="stockFromShoot" :image="image" @loaded="stockFromShootLoaded"/>
            </div>
        </div>
    </div>
</template>
<script>
import ImagesFromShoot from '~/components/stockImage/ImagesFromShoot'
import VideoPlayer from '~/components/global/VideoPlayer.vue'
import { mapGetters } from 'vuex'

export default {
    name: 'StockImage',
    components: {
        VideoPlayer,
        ImagesFromShoot
    },
    props: {
        image: {}
    },
    data () {
        return {
            imageBackgroundStyle: `background-image: url(${this.image.url})`,
            selectedBackgroundColor: null,
            backgroundColors: [
                {
                    name: 'Grey',
                    color: '#736a61'
                },
                {
                    name: 'Light Grey',
                    color: '#c7c7c7'
                },
                {
                    name: 'White',
                    color: '#fff'
                },
                {
                    name: 'Black',
                    color: '#000'
                }
            ],
            videoElapsedTime: null,
            videoTimecodeOffset: 0
        }
    },
    computed: {
        ...mapGetters({
            user: 'auth/user'
        })
    },
    mounted () {
        if (this.image.type === 'video') {
            this.$refs.video.startPlayer({
                autoplay: true,
                controls: false,
                loop: true,
                muted: true,
                poster: this.image.url,
                sources: [
                    {
                        src: this.image.video_url_hls,
                        type: 'application/x-mpegURL'
                    },
                    {
                        src: this.image.video_url,
                        type: 'video/mp4'
                    }
                ]
            })

            window.addEventListener('resize', this.fixTimecodePosition)
        }
    },
    unmounted () {
        if (this.image.type === 'video') {
            window.removeEventListener('resize', this.fixTimecodePosition)
        }
    },
    methods: {
        stockLoaded () {
            this.$emit('loaded')
        },
        stockFromShootLoaded () {
            this.$emit('stockFromShootLoaded')
        },
        timeElapsed (time) {
            this.videoElapsedTime = time
        },
        selectBackgroundColor (color) {
            this.selectedBackgroundColor = color
            this.imageBackgroundStyle = `background-color:${color.color};background-image:none;filter:none;`
        },
        fixTimecodePosition () {
            this.videoTimecodeOffset = this.$refs.video.offsetLeft
        },
        addToCollections () {
            if (this.user) {
                this.$nuxt.$emit('addToCollections', this.image)
            } else {
                this.$nuxt.$emit('showAuthModal', {
                    name: 'addToCollections',
                    payload: this.image
                })
            }
        }
    }
}
</script>

<style scoped>
.vjs-poster, .vjs-title-bar, .vjs-text-track-display, .vjs-loading-spinner, .vjs-big-play-button, .vjs-control-bar, .vjs-error-display, .vjs-modal-dialog {
    display: none !important;
}

.vjs_video_3-dimensions {
    width: unset !important;
    height: 100% !important;
}
</style>
